import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { CSSObjectWithLabel } from 'react-select';

import { useGetAllSessions } from '~entities/session/api/get-sessions';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import {
  type SessionOption,
  SessionSelector,
} from '~features/shared/session-selector';

import { DownloadClassReports } from '~pages/performance-dashboard/ui/download-class-reports';

import { Button } from '~shared/shadcn/ui/button';

import { AgeDemographicsChart } from './age-demographics-chart';
import { AttendanceChart } from './attendance-chart';
import { CapacityChart } from './capacity-chart';
import { DropRateChart } from './drop-rate-chart';
import { MetricStrip } from './metric-strip';
import { StudentDistributionChart } from './student-distribution-chart';

export function PerformanceDashboardPage() {
  const [session, setSession] = useState<SessionOption | null | undefined>(
    null,
  );

  const { data, isFetching } = useGetAllSessions({
    page: 1,
    limit: 10,
  });

  useEffect(() => {
    if (data?.sessions.length && data.sessions[0]) {
      const defaultSession = data.sessions[0];
      setSession({
        label: defaultSession.name,
        value: defaultSession._id,
      });
    }
  }, [data]);

  const studio = useCurrentStudio();

  const customStyles = {
    control: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
      ...base,
      backgroundColor: 'hsl(48, 96%, 90%)', // subtle yellow tint for the whole container
    }),
    placeholder: (base: CSSObjectWithLabel) => ({
      ...base,
      fontWeight: 'bold',
    }),
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      fontWeight: 'bold',
    }),
  };

  return (
    <div className='min-h-screen bg-background'>
      {/* Header */}
      <header className='sticky top-0 z-10 flex h-16 items-center justify-between border-b bg-background px-4'>
        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-2'>
            <Link to='/dashboard'>
              <Button
                variant='ghost'
                className='flex items-center p-2 [&_svg]:size-6'
              >
                <ArrowLeft className='size-6' />
              </Button>
            </Link>
            <div>
              <h1 className='text-lg font-semibold'>Performance Dashboard</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard content */}
      <main className='py-4 md:py-6'>
        <div className='mb-8'>
          <div className='flex items-center justify-between'>
            <h2 className='text-2xl font-bold tracking-tight'>
              {studio?.subaccountName}
            </h2>
            <div className='flex items-center gap-2'>
              <DownloadClassReports sessionId={session?.value} />
              <div className='w-[300px]'>
                <SessionSelector
                  session={session}
                  onChange={setSession}
                  styles={customStyles}
                />
              </div>
            </div>
          </div>
          <p className='mt-1 text-muted-foreground'>
            Monitor your studio&apos;s key performance indicators
          </p>
        </div>

        {/* KPI Summary Cards */}
        <MetricStrip sessionId={session?.value} />

        {/* Main Dashboard Charts */}
        {isFetching ? (
          <div className='flex items-center justify-center'>
            <p className='text-muted-foreground'>Loading...</p>
          </div>
        ) : data?.sessions.length === 0 ? (
          <div className='flex items-center justify-center'>
            <p className='text-muted-foreground'>No sessions found</p>
          </div>
        ) : !session?.value ? (
          <div className='flex items-center justify-center'>
            <p className='text-muted-foreground'>No session selected</p>
          </div>
        ) : (
          <div className='grid gap-4 md:grid-cols-2'>
            <AttendanceChart sessionId={session.value} />
            <CapacityChart sessionId={session.value} />
            <DropRateChart sessionId={session.value} />
            <StudentDistributionChart sessionId={session.value} />
            <AgeDemographicsChart sessionId={session.value} />
          </div>
        )}
      </main>
    </div>
  );
}
